import { formatSaleNumber, uuidv7 } from '@pos/core';
import type { OutboxOp } from '@pos/sync';
import type { LocalDb } from './db.ts';
import type { WriteActor } from './repo.ts';
import type { AccountEntryRow, SaleRow, StockMovementRow } from './types.ts';

/**
 * Guarda una venta (o devolución) en el dispositivo, en UNA transacción:
 *  - le asigna el número siguiente de este dispositivo ("0001-00000124"),
 *  - guarda la venta con sus ítems y pagos,
 *  - guarda sus movimientos de stock y de cuenta corriente (solo locales: el servidor los crea
 *    con los mismos ids),
 *  - y encola la venta como una sola operación.
 * O queda todo, o nada.
 */
export async function recordSaleLocally(
  db: LocalDb,
  sale: Omit<SaleRow, 'number'>,
  deviceCode: number,
  actor: WriteActor,
): Promise<SaleRow> {
  return db.transaction('rw', [db.sales, db.stock_movements, db.account_entries, db.outbox, db.meta], async () => {
    const key = sale.kind === 'return' ? 'return_seq' : 'sale_seq';
    const last = ((await db.meta.get(key))?.value as number | undefined) ?? 0;
    const row: SaleRow = { ...sale, number: formatSaleNumber(deviceCode, last + 1) };
    await db.meta.put({ key, value: last + 1 });
    await db.sales.add(row);
    const movements: StockMovementRow[] = row.items.map((item) => ({
      id: item.movement_id,
      business_id: row.business_id,
      variant_id: item.variant_id,
      type: row.kind === 'return' ? 'customer_return' : 'sale',
      qty_milli: row.kind === 'return' ? item.qty_milli : -item.qty_milli,
      reason: null,
      ref_type: 'sale',
      ref_id: row.id,
      occurred_at: row.occurred_at,
      device_id: row.device_id,
      created_by: row.seller_id,
      created_at: row.created_at,
    }));
    await db.stock_movements.bulkAdd(movements);
    // Cuenta corriente: el servidor crea el movimiento con el id del pago; acá se guarda igual
    // para que el saldo del cliente cambie al instante.
    if (row.customer_id) {
      const entries: AccountEntryRow[] = row.payments
        .filter((p) => p.method === 'account' && p.amount > 0)
        .map((p) => ({
          id: p.id,
          business_id: row.business_id,
          customer_id: row.customer_id!,
          type: row.kind,
          amount: row.kind === 'return' ? -p.amount : p.amount,
          method: null,
          sale_id: row.id,
          cash_session_id: null,
          note: null,
          occurred_at: row.occurred_at,
          device_id: row.device_id,
          created_by: row.seller_id,
          created_at: row.created_at,
        }));
      await db.account_entries.bulkAdd(entries);
    }
    const op: OutboxOp = {
      op_id: uuidv7(),
      business_id: actor.business_id,
      table: 'sales',
      kind: 'insert',
      row_id: row.id,
      payload: row as unknown as Record<string, unknown>,
      created_at: row.created_at,
      created_by: actor.user_id,
      device_id: actor.device_id,
      status: 'pending',
      attempts: 0,
      next_attempt_at: 0,
    };
    await db.outbox.add(op);
    return row;
  });
}

/** Lo ya devuelto de cada línea de una venta (sumando las devoluciones guardadas en este dispositivo). */
export function returnedByItem(returns: SaleRow[]): Map<string, number> {
  const result = new Map<string, number>();
  for (const r of returns) {
    for (const item of r.items) {
      if (item.original_item_id) result.set(item.original_item_id, (result.get(item.original_item_id) ?? 0) + item.qty_milli);
    }
  }
  return result;
}

/**
 * Borra del dispositivo las ventas ya enviadas de más de `days` días (el historial sigue en el
 * servidor). Las pendientes de envío nunca se borran.
 */
export async function pruneOldSales(db: LocalDb, days = 60, now = Date.now()): Promise<number> {
  const cutoff = new Date(now - days * 86_400_000).toISOString();
  const old = await db.sales.where('occurred_at').below(cutoff).filter((s) => s.server_xid !== undefined).primaryKeys();
  await db.sales.bulkDelete(old);
  return old.length;
}
